import { supabase } from './supabase';

export const playoffService = {
  async getPlayoffSettings(seasonId) {
    try {
      const { data, error } = await supabase
        .from('playoff_settings')
        .select('*')
        .eq('season_id', seasonId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data || {
        playoffs_enabled: false,
        playoff_team_count: 8,
        playoff_format_type: 'single_elimination'
      };
    } catch (err) {
      console.error('Error fetching playoff settings:', err);
      throw err;
    }
  },

  async updatePlayoffSettings(seasonId, updates) {
    try {
      let settings = await this.getPlayoffSettings(seasonId);

      if (!settings.id) {
        const { data, error } = await supabase
          .from('playoff_settings')
          .insert([{
            season_id: seasonId,
            ...updates
          }])
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('playoff_settings')
          .update(updates)
          .eq('season_id', seasonId)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (err) {
      console.error('Error updating playoff settings:', err);
      throw err;
    }
  },

  async getPlayoffTeams(playoffSettingsId) {
    try {
      const { data, error } = await supabase
        .from('playoff_teams')
        .select(`
          *,
          teams (
            id,
            name,
            abbreviation,
            logo_url,
            conferences (
              id,
              name,
              abbreviation
            )
          )
        `)
        .eq('playoff_settings_id', playoffSettingsId)
        .order('seed_number', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching playoff teams:', err);
      throw err;
    }
  },

  async setPlayoffTeams(playoffSettingsId, teams) {
    try {
      await supabase
        .from('playoff_teams')
        .delete()
        .eq('playoff_settings_id', playoffSettingsId);

      if (teams.length === 0) {
        return [];
      }

      const { data, error } = await supabase
        .from('playoff_teams')
        .insert(teams.map(t => ({
          playoff_settings_id: playoffSettingsId,
          team_id: t.team_id || t.id,
          seed_number: t.seed_number
        })))
        .select();

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error setting playoff teams:', err);
      throw err;
    }
  },

  async generateBracket(playoffSettingsId, formatType, teamCount) {
    try {
      const playoffTeams = await this.getPlayoffTeams(playoffSettingsId);

      if (playoffTeams.length < teamCount) {
        throw new Error(`Not enough teams seeded. Expected ${teamCount}, got ${playoffTeams.length}`);
      }

      await supabase
        .from('playoff_matchups')
        .delete()
        .eq('playoff_settings_id', playoffSettingsId);

      if (formatType === 'single_elimination') {
        return await this._generateSingleElimination(playoffSettingsId, playoffTeams, teamCount);
      } else if (formatType === 'double_elimination') {
        return await this._generateDoubleElimination(playoffSettingsId, playoffTeams, teamCount);
      } else if (formatType.startsWith('best_of')) {
        const boType = parseInt(formatType.split('_')[2]);
        return await this._generateBestOfSeries(playoffSettingsId, playoffTeams, teamCount, boType);
      }

      throw new Error(`Unsupported playoff format: ${formatType}`);
    } catch (err) {
      console.error('Error generating bracket:', err);
      throw err;
    }
  },

  async _generateSingleElimination(playoffSettingsId, playoffTeams, teamCount) {
    const matchups = [];
    const numRounds = Math.ceil(Math.log2(teamCount));

    for (let round = 1; round <= numRounds; round++) {
      const matchupsInRound = Math.pow(2, numRounds - round);

      if (round === 1) {
        for (let i = 0; i < teamCount / 2; i++) {
          const seed1 = i;
          const seed2 = teamCount - 1 - i;

          matchups.push({
            playoff_settings_id: playoffSettingsId,
            round_number: round,
            matchup_number: i + 1,
            team1_id: playoffTeams[seed1]?.team_id || playoffTeams[seed1]?.teams?.id,
            team2_id: playoffTeams[seed2]?.team_id || playoffTeams[seed2]?.teams?.id,
            team1_seed: seed1 + 1,
            team2_seed: seed2 + 1,
            winner_id: null,
            team1_score: null,
            team2_score: null,
            is_completed: false
          });
        }
      } else {
        for (let i = 0; i < matchupsInRound; i++) {
          matchups.push({
            playoff_settings_id: playoffSettingsId,
            round_number: round,
            matchup_number: i + 1,
            team1_id: null,
            team2_id: null,
            team1_seed: null,
            team2_seed: null,
            winner_id: null,
            team1_score: null,
            team2_score: null,
            is_completed: false
          });
        }
      }
    }

    const { data, error } = await supabase
      .from('playoff_matchups')
      .insert(matchups)
      .select();

    if (error) throw error;
    return data || [];
  },

  async _generateDoubleElimination(playoffSettingsId, playoffTeams, teamCount) {
    const matchups = [];
    const numRounds = Math.ceil(Math.log2(teamCount));

    for (let round = 1; round <= numRounds * 2 - 1; round++) {
      if (round <= numRounds) {
        const matchupsInRound = Math.pow(2, numRounds - round);

        if (round === 1) {
          for (let i = 0; i < teamCount / 2; i++) {
            const seed1 = i;
            const seed2 = teamCount - 1 - i;

            matchups.push({
              playoff_settings_id: playoffSettingsId,
              round_number: round,
              matchup_number: i + 1,
              team1_id: playoffTeams[seed1]?.team_id || playoffTeams[seed1]?.teams?.id,
              team2_id: playoffTeams[seed2]?.team_id || playoffTeams[seed2]?.teams?.id,
              team1_seed: seed1 + 1,
              team2_seed: seed2 + 1,
              winner_id: null,
              team1_score: null,
              team2_score: null,
              is_completed: false
            });
          }
        } else {
          for (let i = 0; i < matchupsInRound; i++) {
            matchups.push({
              playoff_settings_id: playoffSettingsId,
              round_number: round,
              matchup_number: i + 1,
              team1_id: null,
              team2_id: null,
              team1_seed: null,
              team2_seed: null,
              winner_id: null,
              team1_score: null,
              team2_score: null,
              is_completed: false
            });
          }
        }
      } else {
        const losersBracketRound = round - numRounds;
        const matchupsInRound = Math.pow(2, numRounds - losersBracketRound);

        for (let i = 0; i < matchupsInRound; i++) {
          matchups.push({
            playoff_settings_id: playoffSettingsId,
            round_number: round,
            matchup_number: i + 1,
            team1_id: null,
            team2_id: null,
            team1_seed: null,
            team2_seed: null,
            winner_id: null,
            team1_score: null,
            team2_score: null,
            is_completed: false
          });
        }
      }
    }

    const { data, error } = await supabase
      .from('playoff_matchups')
      .insert(matchups)
      .select();

    if (error) throw error;
    return data || [];
  },

  async _generateBestOfSeries(playoffSettingsId, playoffTeams, teamCount, bestOfValue) {
    const matchups = [];
    const numRounds = Math.ceil(Math.log2(teamCount));

    for (let round = 1; round <= numRounds; round++) {
      const matchupsInRound = Math.pow(2, numRounds - round);

      if (round === 1) {
        for (let i = 0; i < teamCount / 2; i++) {
          const seed1 = i;
          const seed2 = teamCount - 1 - i;

          matchups.push({
            playoff_settings_id: playoffSettingsId,
            round_number: round,
            matchup_number: i + 1,
            team1_id: playoffTeams[seed1]?.team_id || playoffTeams[seed1]?.teams?.id,
            team2_id: playoffTeams[seed2]?.team_id || playoffTeams[seed2]?.teams?.id,
            team1_seed: seed1 + 1,
            team2_seed: seed2 + 1,
            winner_id: null,
            team1_score: null,
            team2_score: null,
            is_completed: false
          });
        }
      } else {
        for (let i = 0; i < matchupsInRound; i++) {
          matchups.push({
            playoff_settings_id: playoffSettingsId,
            round_number: round,
            matchup_number: i + 1,
            team1_id: null,
            team2_id: null,
            team1_seed: null,
            team2_seed: null,
            winner_id: null,
            team1_score: null,
            team2_score: null,
            is_completed: false
          });
        }
      }
    }

    const { data, error } = await supabase
      .from('playoff_matchups')
      .insert(matchups)
      .select();

    if (error) throw error;
    return data || [];
  },

  async getMatchups(playoffSettingsId) {
    try {
      const { data, error } = await supabase
        .from('playoff_matchups')
        .select(`
          *,
          team1:team1_id (
            id,
            name,
            abbreviation,
            logo_url
          ),
          team2:team2_id (
            id,
            name,
            abbreviation,
            logo_url
          ),
          winner:winner_id (
            id,
            name,
            abbreviation,
            logo_url
          )
        `)
        .eq('playoff_settings_id', playoffSettingsId)
        .order('round_number', { ascending: true })
        .order('matchup_number', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching matchups:', err);
      throw err;
    }
  },

  async updateMatchup(matchupId, updates) {
    try {
      const { data, error } = await supabase
        .from('playoff_matchups')
        .update(updates)
        .eq('id', matchupId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error updating matchup:', err);
      throw err;
    }
  },

  async getSeriesGames(matchupId) {
    try {
      const { data, error } = await supabase
        .from('playoff_series_games')
        .select(`
          *,
          winner:winner_id (
            id,
            name,
            abbreviation,
            logo_url
          )
        `)
        .eq('matchup_id', matchupId)
        .order('game_number', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching series games:', err);
      throw err;
    }
  },

  async addSeriesGame(matchupId, gameNumber, team1Score, team2Score, winnerId = null) {
    try {
      const { data, error } = await supabase
        .from('playoff_series_games')
        .insert([{
          matchup_id: matchupId,
          game_number: gameNumber,
          team1_score: team1Score,
          team2_score: team2Score,
          winner_id: winnerId,
          is_completed: winnerId !== null
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error adding series game:', err);
      throw err;
    }
  },

  async updateSeriesGame(gameId, team1Score, team2Score, winnerId) {
    try {
      const { data, error } = await supabase
        .from('playoff_series_games')
        .update({
          team1_score: team1Score,
          team2_score: team2Score,
          winner_id: winnerId,
          is_completed: winnerId !== null
        })
        .eq('id', gameId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error updating series game:', err);
      throw err;
    }
  },

  async validateTeamCount(seasonId, playoffTeamCount) {
    try {
      const { count, error } = await supabase
        .from('team_standings')
        .select('*', { count: 'exact', head: 0 })
        .eq('season_id', seasonId);

      if (error) throw error;

      if (playoffTeamCount > count) {
        throw new Error(`Playoff team count (${playoffTeamCount}) exceeds total teams in season (${count})`);
      }

      return true;
    } catch (err) {
      console.error('Error validating team count:', err);
      throw err;
    }
  }
};
